import { chalkLine, chalkText } from '../../primitives/chalk';

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function pointLerp(p1, p2, t) {
  return { x: lerp(p1.x, p2.x, t), y: lerp(p1.y, p2.y, t) };
}

function normalizeNode(rawNode, area) {
  return {
    id: String(rawNode.id),
    label: String(rawNode.label || rawNode.id),
    x: area.x + area.w * (rawNode.xk ?? 0.5),
    y: area.y + area.h * (rawNode.yk ?? 0.5),
  };
}

function normalizeEdge(rawEdge, nodeMap) {
  const from = nodeMap.get(String(rawEdge.from));
  const to = nodeMap.get(String(rawEdge.to));
  if (!from || !to) return null;
  return {
    from,
    to,
    label: rawEdge.label ? String(rawEdge.label) : '',
  };
}

function drawArrowHead(ctx, from, to, alpha = 0.85) {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const headLen = 10;
  const left = {
    x: to.x - headLen * Math.cos(angle - Math.PI / 6),
    y: to.y - headLen * Math.sin(angle - Math.PI / 6),
  };
  const right = {
    x: to.x - headLen * Math.cos(angle + Math.PI / 6),
    y: to.y - headLen * Math.sin(angle + Math.PI / 6),
  };
  chalkLine(ctx, to.x, to.y, left.x, left.y, 1.5, alpha);
  chalkLine(ctx, to.x, to.y, right.x, right.y, 1.5, alpha);
}

function drawLabelBadge(ctx, text, x, y, size) {
  const safeText = String(text || '');
  if (!safeText) return;

  ctx.save();
  ctx.font = `${size}px sans-serif`;
  ctx.textAlign = 'center';
  const textWidth = Math.max(10, ctx.measureText(safeText).width);
  const padX = 8;
  const padY = 5;
  const width = textWidth + padX * 2;
  const height = size + padY * 2;
  const rectX = x - width / 2;
  const rectY = y - size * 0.8 - padY;

  ctx.fillStyle = 'rgba(14, 26, 38, 0.88)';
  ctx.strokeStyle = 'rgba(255, 165, 54, 0.74)';
  ctx.lineWidth = 1.25;
  ctx.fillRect(rectX, rectY, width, height);
  ctx.strokeRect(rectX, rectY, width, height);
  ctx.restore();

  chalkText(ctx, safeText, x, y, size, 0.98, 'sans-serif', 'center', { role: 'diagramLabel' });
}

export async function prepareSketchPathNode(env, spec) {
  const area = env.boardArea();
  const nodes = Array.isArray(spec.nodes) ? spec.nodes.map((n) => normalizeNode(n, area)) : [];
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const edges = Array.isArray(spec.edges)
    ? spec.edges.map((e) => normalizeEdge(e, nodeMap)).filter(Boolean)
    : [];

  const draw = spec.draw || {};
  return {
    area,
    nodes,
    edges,
    title: spec.title ? String(spec.title) : '',
    titleSize: env.getStyle('bodySize'),
    labelSize: env.getStyle('smallSize'),
    secPerEdge: draw.secPerEdge || 0.5,
    secPerNode: draw.secPerNode || 0.28,
    showArrowheads: draw.showArrowheads ?? true,
    interactive: draw.interactive ?? false,
  };
}

export function commitSketchPathNode(packet, ctxs, anim) {
  let t = 0;
  let draggingNodeIndex = -1;
  let dragOffsetX = 0;
  let dragOffsetY = 0;
  let done = false;
  let listenersAttached = false;
  const canvas = ctxs.ink.canvas;

  const attachListeners = () => {
    if (listenersAttached || !packet.interactive || !canvas) return;
    listenersAttached = true;

    const toCanvasPoint = (event) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
      const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
      const dpr = window.devicePixelRatio || 1;
      return {
        x: (event.clientX - rect.left) * (scaleX / dpr),
        y: (event.clientY - rect.top) * (scaleY / dpr),
      };
    };

    const hitNode = (p) => {
      const radius = 18;
      for (let i = packet.nodes.length - 1; i >= 0; i--) {
        const n = packet.nodes[i];
        const dx = p.x - n.x;
        const dy = p.y - n.y;
        if (dx * dx + dy * dy <= radius * radius) return i;
      }
      return -1;
    };

    const onPointerDown = (event) => {
      if (!ctxs.interaction?.isPaused?.()) return;
      if (!packet.interactive) return;

      const p = toCanvasPoint(event);
      const idx = hitNode(p);
      if (idx < 0) return;

      draggingNodeIndex = idx;
      dragOffsetX = p.x - packet.nodes[idx].x;
      dragOffsetY = p.y - packet.nodes[idx].y;
      canvas.setPointerCapture?.(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (draggingNodeIndex < 0) return;
      if (!ctxs.interaction?.isPaused?.()) return;

      const p = toCanvasPoint(event);
      const node = packet.nodes[draggingNodeIndex];
      const radius = 18;

      node.x = Math.max(
        packet.area.x + radius,
        Math.min(packet.area.x + packet.area.w - radius, p.x - dragOffsetX)
      );
      node.y = Math.max(
        packet.area.y + radius,
        Math.min(packet.area.y + packet.area.h - radius, p.y - dragOffsetY)
      );
    };

    const onPointerUp = (event) => {
      draggingNodeIndex = -1;
      canvas.releasePointerCapture?.(event.pointerId);
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);

    packet._cleanupListeners = () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
    };
  };

  attachListeners();

  anim.add((dt) => {
    t += dt;
    ctxs.ink.clearRect(packet.area.x - 4, packet.area.y - 4, packet.area.w + 8, packet.area.h + 8);

    if (packet.title) {
      chalkText(
        ctxs.ink,
        packet.title,
        packet.area.x + packet.area.w / 2,
        packet.area.y + 26,
        packet.titleSize,
        0.9,
        'sans-serif',
        'center'
      );
    }

    let cursor = 0;
    let finishedEdges = 0;
    for (let i = 0; i < packet.edges.length; i++) {
      const edge = packet.edges[i];
      const start = cursor;
      const end = cursor + packet.secPerEdge;
      cursor = end;

      if (t <= start) continue;
      const p = Math.min((t - start) / packet.secPerEdge, 1);
      const head = pointLerp(edge.from, edge.to, p);
      chalkLine(ctxs.ink, edge.from.x, edge.from.y, head.x, head.y, 2, 0.88);

      if (p >= 1 && packet.showArrowheads) {
        drawArrowHead(ctxs.ink, edge.from, edge.to, 0.8);
      }
      if (p >= 1) {
        finishedEdges++;
        if (edge.label) {
          const lx = (edge.from.x + edge.to.x) / 2;
          const ly = (edge.from.y + edge.to.y) / 2 - 8;
          drawLabelBadge(ctxs.ink, edge.label, lx, ly, packet.labelSize * 0.94);
        }
      }
    }

    let finishedNodes = 0;
    for (let i = 0; i < packet.nodes.length; i++) {
      const node = packet.nodes[i];
      const start = cursor + i * packet.secPerNode;
      const progress = Math.min(Math.max((t - start) / packet.secPerNode, 0), 1);
      if (progress <= 0) continue;

      const radius = 18;
      ctxs.ink.save();
      ctxs.ink.strokeStyle = 'rgba(245, 250, 255, 0.9)';
      ctxs.ink.fillStyle = 'rgba(245, 250, 255, 0.08)';
      ctxs.ink.lineWidth = 2;
      ctxs.ink.beginPath();
      ctxs.ink.arc(node.x, node.y, radius, 0, Math.PI * 2 * progress);
      ctxs.ink.fill();
      ctxs.ink.stroke();
      ctxs.ink.restore();

      if (progress >= 1) {
        finishedNodes++;
        drawLabelBadge(
          ctxs.ink,
          node.label,
          node.x,
          node.y + 18 + packet.labelSize + 4,
          packet.labelSize
        );
      }
    }

    done = finishedEdges >= packet.edges.length && finishedNodes >= packet.nodes.length;

    const shouldHoldForPausedInteraction =
      packet.interactive && done && ctxs.interaction?.isPaused?.();
    if (shouldHoldForPausedInteraction) {
      return true;
    }

    if (done) {
      packet._cleanupListeners?.();
      return false;
    }

    return true;
  });
}
